import { Link } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useDashboardMetrics } from '../hooks/useDashboard'
import { TrendingUp, ChevronRight } from 'lucide-react'
import { cn, formatCurrency } from '@/lib/utils'

/**
 * Pipeline stage configuration
 * Defines display order, labels, and colors for each deal stage
 */
const PIPELINE_STAGES = [
  { key: 'lead', label: 'Lead', color: 'bg-slate-400' },
  { key: 'qualified', label: 'Qualified', color: 'bg-blue-500' },
  { key: 'proposal', label: 'Proposal', color: 'bg-purple-500' },
  { key: 'negotiation', label: 'Negotiation', color: 'bg-orange-500' },
  { key: 'closed_won', label: 'Won', color: 'bg-green-500' },
  { key: 'closed_lost', label: 'Lost', color: 'bg-red-500' },
] as const

/**
 * PipelineWidget Component
 *
 * Displays a visual representation of the deals pipeline.
 * Shows the number of deals and total value at each stage.
 */
export function PipelineWidget() {
  const { data: metrics, isLoading, error } = useDashboardMetrics()

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-primary" />
              <CardTitle className="text-base">Sales Pipeline</CardTitle>
            </div>
          </div>
          <CardDescription>Deal distribution by stage</CardDescription>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-24" />
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Sales Pipeline</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Failed to load pipeline data</p>
        </CardContent>
      </Card>
    )
  }

  const pipeline = metrics?.pipeline || []

  // Build stage data map for quick lookup
  const stageDataMap = new Map(pipeline.map((s) => [s.stage, s]))

  // Calculate total for percentage bars (exclude closed stages)
  const activeStages = ['lead', 'qualified', 'proposal', 'negotiation']
  const totalActiveValue = pipeline
    .filter((s) => activeStages.includes(s.stage))
    .reduce((sum, s) => sum + s.value, 0)

  // Calculate max count for bar scaling
  const maxCount = Math.max(...pipeline.map((s) => s.count), 1)

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-primary" />
            <CardTitle className="text-base">Sales Pipeline</CardTitle>
          </div>
          <Link
            to="/dashboard/deals"
            className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1"
          >
            View deals
            <ChevronRight className="h-3 w-3" />
          </Link>
        </div>
        <CardDescription>Deal distribution by stage</CardDescription>
      </CardHeader>
      <CardContent>
        {pipeline.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4 text-center">
            No deals in pipeline yet
          </div>
        ) : (
          <div className="space-y-3">
            {/* Active pipeline stages */}
            <div className="space-y-2">
              {PIPELINE_STAGES.filter((s) => activeStages.includes(s.key)).map((stage) => {
                const data = stageDataMap.get(stage.key)
                const count = data?.count || 0
                const value = data?.value || 0
                const barWidth = maxCount > 0 ? (count / maxCount) * 100 : 0

                return (
                  <Link
                    key={stage.key}
                    to={`/dashboard/deals?stage=${stage.key}`}
                    className="block group"
                  >
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="font-medium group-hover:text-primary transition-colors">
                        {stage.label}
                      </span>
                      <span className="text-muted-foreground">
                        {count} {count === 1 ? 'deal' : 'deals'} · {formatCurrency(value, 'AUD', { compact: true })}
                      </span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className={cn('h-full rounded-full transition-all', stage.color)}
                        style={{ width: `${Math.max(barWidth, count > 0 ? 5 : 0)}%` }}
                      />
                    </div>
                  </Link>
                )
              })}
            </div>

            {/* Closed stages summary */}
            <div className="pt-2 border-t flex gap-4">
              {PIPELINE_STAGES.filter((s) => s.key === 'closed_won' || s.key === 'closed_lost').map(
                (stage) => {
                  const data = stageDataMap.get(stage.key)
                  const count = data?.count || 0
                  const value = data?.value || 0

                  return (
                    <Link
                      key={stage.key}
                      to={`/dashboard/deals?stage=${stage.key}`}
                      className="flex-1 text-center group"
                    >
                      <div
                        className={cn(
                          'inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium transition-opacity group-hover:opacity-80',
                          stage.key === 'closed_won'
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                        )}
                      >
                        <span className={cn('w-2 h-2 rounded-full', stage.color)} />
                        {count} {stage.label}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{formatCurrency(value, 'AUD', { compact: true })}</p>
                    </Link>
                  )
                }
              )}
            </div>

            {/* Pipeline value summary */}
            {totalActiveValue > 0 && (
              <div className="text-center pt-2 border-t">
                <p className="text-xs text-muted-foreground">Active Pipeline Value</p>
                <p className="text-lg font-bold">{formatCurrency(totalActiveValue, 'AUD', { compact: true })}</p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
